import React, { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { Sidebar } from "./components/layout/Sidebar";
import { TopBar } from "./components/layout/TopBar";
import { useAuthStore } from "./lib/auth";
import { useThemeStore } from "./lib/theme";
import { hospitalsApi } from "./lib/api";
import Dashboard from "./pages/Dashboard";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";

/* ─── Lazy pages ──────────────────────────────────────────────── */
const PatientsPage  = React.lazy(() => import("./pages/PatientsPage"));
const AlertsPage    = React.lazy(() => import("./pages/AlertsPage"));
const ReportsPage   = React.lazy(() => import("./pages/ReportsPage"));
const SettingsPage  = React.lazy(() => import("./pages/SettingsPage"));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const { data: statsData } = useQuery({
    queryKey: ["stats", user?.hospital_id],
    queryFn: () => hospitalsApi.stats(user!.hospital_id!).then(r => r.data),
    enabled: !!user?.hospital_id,
    refetchInterval: 30_000,
  });
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden font-sans transition-colors duration-200">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar pendingAlerts={statsData?.pending_alerts ?? 0} />
        <main className="flex-1 overflow-y-auto p-6">
          <React.Suspense fallback={
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }>
            <Outlet />
          </React.Suspense>
        </main>
      </div>
    </div>
  );
}

function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <AppLayout />;
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const isDark = useThemeStore((s) => s.isDark);
  useLayoutEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDark]);
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route element={<RequireAuth />}>
              <Route path="/home" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/patients"  element={<PatientsPage />} />
              <Route path="/alerts"    element={<AlertsPage />} />
              <Route path="/reports"   element={<ReportsPage />} />
              <Route path="/settings"  element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>

          <Toaster
            position="top-right"
            toastOptions={{
              className: "text-sm font-medium",
              success: { iconTheme: { primary: "#22c55e", secondary: "#fff" } },
              error:   { iconTheme: { primary: "#ef4444", secondary: "#fff" } },
            }}
          />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
